import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Download,
  Visibility,
  PhotoLibrary,
  Palette,
  AddPhotoAlternate,
  Info,
  Delete
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useBook } from '../../Context/BookContext';
import { getPresignedBookUrl, authenticatedPost } from '../../utils/api';
import { SnackbarState } from '../../types';
import { useAuth } from '../../Context/AuthContext';
import axios from 'axios';

const removeBookEndpoint = process.env.REACT_APP_LAMBDA_REMOVE_BOOK;

interface DeleteResponse {
  message: string;
  details: Record<string, number>;
}

const DownloadReadStage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBook, setSelectedBook, refreshTrigger, setRefreshTrigger } = useBook();
  const { user } = useAuth();
  
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const handleCloseSnackbar = (): void => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDownloadBook = async (): Promise<void> => {
    if (!selectedBook) {
      setSnackbar({
        open: true,
        message: 'Please select a book first',
        severity: 'error'
      });
      return;
    }
    try {
      setIsDownloading(true);
      const { presignedUrl } = await getPresignedBookUrl(selectedBook.FileId, undefined, true);
      window.location.assign(presignedUrl);
      setSnackbar({
        open: true,
        message: 'Download started!',
        severity: 'success'
      });
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to get download link';
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeleteBook = async (): Promise<void> => {
    if (!selectedBook || !removeBookEndpoint) return;
    
    const bookIdToDelete = selectedBook.FileId;
    setIsDeleting(true);
    
    try {
      console.log(`Sending delete request for book: ${bookIdToDelete}`);
      const response = await authenticatedPost<DeleteResponse>(removeBookEndpoint, {
        user_id: user?.userId,
        file_id: bookIdToDelete
      });
      
      if (response.data && response.status === 200) {
        console.log('Delete response:', response.data);
        const { message, details } = response.data;
        
        console.log(`Success: ${message}`);
        console.log('Deletion details:', details);
        
        const itemsRemoved = Object.values(details).filter(v => typeof v === 'number').reduce((a, b) => a + b, 0);
        setSnackbar({
          open: true,
          message: `Successfully deleted book. ${itemsRemoved} items removed.`,
          severity: 'success'
        });
        
        // Clear the selected book and navigate back to library
        setSelectedBook(null);
        setDeleteDialogOpen(false);
        setRefreshTrigger(refreshTrigger + 1);
        
        // Navigate to library after a short delay
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        console.warn('Unexpected response format:', response);
        setSnackbar({
          open: true,
          message: 'Book was deleted, but the response format was unexpected.',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Error deleting book:', error);
      let errorMessage = 'Unknown error';
      if (axios.isAxiosError(error)) {
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        errorMessage = error.response?.data?.message || error.message;
      }
      setSnackbar({
        open: true,
        message: `Failed to delete book: ${errorMessage}. Please try again.`,
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = (): void => {
    if (selectedBook) {
      setDeleteDialogOpen(true);
    }
  };

  // Check if user should generate more images
  const needsMoreImages = selectedBook && 
    selectedBook.RecommendedQuantity && 
    (selectedBook.ImageCount || 0) < selectedBook.RecommendedQuantity;

  return (
    <Box>
      {!selectedBook ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please select a book from the library or upload a new one to access download and reading options.
        </Alert>
      ) : (
        <>
          {/* Book Title Section */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
              {selectedBook.Title || 'Unnamed Book'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <PhotoLibrary sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {selectedBook.ImageCount || 0}
                {selectedBook.RecommendedQuantity && (
                  <Typography component="span" sx={{ color: 'text.disabled', ml: 0.5 }}>
                    / {selectedBook.RecommendedQuantity} images
                  </Typography>
                )}
              </Typography>
            </Box>
          </Box>

          {/* All Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Download Button */}
                <Button
                  variant="contained"
                  size="large"
                  startIcon={isDownloading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Download />}
                  onClick={handleDownloadBook}
                  disabled={isDownloading}
                  fullWidth
                  sx={{
                    backgroundColor: 'success.main',
                    '&:hover': { 
                      backgroundColor: 'success.dark',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)'
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 2
                  }}
                >
                  {isDownloading ? 'Preparing Download...' : 'Download Book'}
                </Button>

                {/* Read Online Button */}
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Visibility />}
                  onClick={() => navigate('/book-reader')}
                  disabled={selectedBook.ProcessingStatus !== 'completed'}
                  fullWidth
                  sx={{
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': { 
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.main',
                      color: 'white'
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 2
                  }}
                >
                  Read Online
                </Button>

                {/* View Images Button */}
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<PhotoLibrary />}
                  onClick={() => navigate('/image-generator')}
                  fullWidth
                  sx={{
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': { 
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.main',
                      color: 'white'
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 2
                  }}
                >
                  View Images
                </Button>

                {/* Adjust Styling Button */}
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Palette />}
                  onClick={() => navigate('/process-book?stage=styling')}
                  fullWidth
                  sx={{
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': { 
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.main',
                      color: 'white'
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 2
                  }}
                >
                  Adjust Styling
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Generate More Images Alert */}
          {selectedBook.RecommendedQuantity && (
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                  {needsMoreImages ? 'Generate More Images' : 'Image Generation Complete'}
                </Typography>
                {needsMoreImages ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                      You have {selectedBook.ImageCount || 0} of {selectedBook.RecommendedQuantity} recommended images. 
                      We recommend {selectedBook.RecommendedQuantity} images to fully illustrate your entire book.
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<AddPhotoAlternate />}
                      onClick={() => navigate('/image-generator')}
                      fullWidth
                      sx={{
                        backgroundColor: 'secondary.main',
                        color: 'white',
                        '&:hover': { 
                          backgroundColor: 'secondary.dark',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(166, 189, 197, 0.3)'
                        },
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 2
                      }}
                    >
                      Generate Images
                    </Button>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                    ✅ All {selectedBook.RecommendedQuantity} recommended images have been generated for your book.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Delete Book Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}>
                Delete Book
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Permanently delete this book and all associated data including images, summaries, and subjects.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<Delete />}
                onClick={openDeleteDialog}
                disabled={isDeleting}
                fullWidth
                sx={{
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': { 
                    backgroundColor: 'error.dark',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(244, 67, 54, 0.3)'
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 2
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete Book'}
              </Button>
            </CardContent>
          </Card>

          {/* How This Works Info Box */}
          <Card sx={{ 
            mt: 4,
            border: '2px solid',
            borderColor: 'info.main',
            backgroundColor: 'info.light'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Info sx={{ color: 'info.main', fontSize: 28 }} />
                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  How This Works
                </Typography>
              </Box>
              
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                Your illustrated book is now complete and ready to enjoy! 
                All generated images have been automatically integrated into your EPUB file at contextually relevant moments throughout the story. 
                You can download the file to read on any EPUB-compatible device or e-reader like Kindle, Kobo, Apple Books, and more. 
                You can also read your book directly in our online reader, view all your images in the gallery, or go back to adjust the styling and generate additional images if you'd like. 
                Everything is flexible, so you can continue refining your book until it's exactly how you want it.
              </Typography>
            </CardContent>
          </Card>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Book</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedBook?.Title || 'this book'}"? This action cannot be undone.
            All book data, including summaries, subjects, and images will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary" disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteBook} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Delete />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DownloadReadStage;

